<script lang="ts">
  import type { Product } from "../../helpers/types";
  import { roundOf } from "../../helpers/priceCalculator";
  import ComparePrices from "./ComparePrices.svelte";
  import format from "date-fns/format";
  import sv from "date-fns/locale/sv";
  import Text from "../atoms/Text.svelte";
  import InfoButton from "./InfoButton.svelte";

  const {
    product,
    defaultOpen = false,
    expandable = true,
    showPriceInfoButton = true,
    showComparePrice = true,
  } = $props<{
    product: Product;
    defaultOpen?: boolean;
    expandable?: boolean;
    showPriceInfoButton?: boolean;
    showComparePrice?: boolean;
  }>();

  const costs = $derived(product.costs);
  const product_id = `product-specifiction-${product.id}`;
  const discount = $derived(costs.discount != undefined);
  const fixedWithTax = $derived(roundOf(costs.fixedCost * (costs.vat / 100 + 1), 1));
  const kwhCost = $derived(
    roundOf((costs.kwhCost - (costs.surcharge === undefined ? 0 : costs.surcharge)) * 100, 1000)
  );

  let viewSpecification = $state(defaultOpen || !expandable);
</script>

{#if product.type === "variable" || product.type === "hourly"}
  <div class={expandable ? "my-2" : ""}>
    <Text variant="secondary" size="S" class="italic text-left">
      Pris baserat på {product.previousMonth ? format( product.previousMonth, "MMMM", {
        locale: sv,
      }): "föregående"} månads priser.
    </Text>
  </div>
{/if}
<div
  id={product_id}
  class="transition-maxHeight mt-2 w-full md:flex md:space-x-2 md:justify-between {viewSpecification
    ? 'max-h-96 duration-700'
    : 'max-h-0 duration-300'} overflow-hidden"
>
  <table class="text-left text-S overflow-hidden purchaseflow-specification-table">
    <thead>
      <tr>
        <th class="text-H7 font-bold pr-7">Specifikation</th>
        <th></th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td class="flex space-x-1 {viewSpecification ? 'absolute z-10' : ''}">
          <Text id="elpris-text" variant="primary">Elpris exkl moms</Text>
          {#if (product.type === "variable" || product.type === "hourly") && showPriceInfoButton && viewSpecification}
            <InfoButton iconSize={15} class="text-Neutral700 z-10" describedBy="elpris-text">
              Rörligt elpris och elcertifikatsavgift för {format(product.previousMonth, "MMMM", {
                locale: sv,
              })} månad
            </InfoButton>
          {/if}
        </td>
        <td>{kwhCost} öre/kWh</td>
      </tr>
      {#if costs.surcharge}
        <tr>
          <td>Påslag</td>
          <td>{roundOf(costs.surcharge * 100, 1000)} öre/kWh</td>
        </tr>
      {/if}
      {#if discount && costs.discount.kwhLength}
        <tr class="text-Red900">
          <td class="pl-3"> Rabatt {costs.discount.kwhLength * 10} mån </td>
          <td> {roundOf((costs.discount.kwhCost - costs.kwhCost) * 100)} öre/kWh </td>
        </tr>
        <tr>
          <td>Moms {costs.vat}%</td>
          <td> {roundOf(costs.discount.kwhVat * 100, 1000)} öre/kWh</td>
        </tr>
      {:else}
        <tr>
          <td>Moms {costs.vat}%</td>
          <td> {roundOf(costs.kwhVat * 100, 1000)} öre/kWh</td>
        </tr>
      {/if}
      <tr>
        <td class="pr-2 md:pr-8"> Månadsavgift inkl moms </td>
        <td> {fixedWithTax} kr/mån </td>
      </tr>
      {#if discount && costs.discount.fixedLength}
        <tr class="text-Red900">
          <td class="pl-3"> Rabatt {costs.discount.fixedLength * 10} mån </td>
          <td
            >{roundOf(costs.discount.fixedCost * (costs.vat / 100 + 1), 1) - fixedWithTax} kr/mån</td
          >
        </tr>
      {/if}
    </tbody>
  </table>
  {#if showComparePrice}
    <div class="mt-4 md:mt-0">
      <span class="text-sm font-semibold">Jämförpris</span>
      <ComparePrices {product} />
    </div>
  {/if}
</div>
{#if expandable}
  <button
    type="button"
    aria-controls={product_id}
    aria-expanded={viewSpecification}
    class="text-left text-S self-end flex items-center text-Neutral800"
    onclick={() => (viewSpecification = !viewSpecification)}
  >
    <span class="flex-shrink-0 {viewSpecification ? 'minus' : 'plus'}"></span>
    <span class="mt-0.5">Uppskattad kostnad. Se hur vi beräknat priset</span>
  </button>
{/if}
